import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Target, ArrowRight } from "lucide-react";
import MomentBanner from "@/components/MomentBanner";

/**
 * Transition overlay shown after WarmUp completes, before Practice Pool A begins.
 * Modelled exactly after PoolACompleteScreen — same layout, card style, typography.
 */
export default function WarmUpToPracticeScreen({ payload, onProceed }) {
  const subTopic = payload?.sub_topic || "this sub-topic";
  const moment = payload?.mitraa_moment;

  const [sparks] = useState(() =>
    Array.from({ length: 18 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 1.6 + Math.random() * 1.2,
      hue: [160, 180, 140, 170, 155][i % 5],
    }))
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4 py-8"
      data-testid="warmup-to-practice-overlay"
    >
      {/* Spark layer */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {sparks.map((s) => (
          <span
            key={s.id}
            className="absolute block w-2.5 h-2.5 rounded-sm spark-fall"
            style={{
              left: `${s.left}%`,
              top: "-10px",
              backgroundColor: `hsl(${s.hue} 80% 55%)`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      <Card
        className="relative z-10 rounded-3xl p-8 sm:p-10 max-w-lg w-full mit-shadow border-emerald-200 bg-white text-center fade-up"
        data-testid="warmup-to-practice-card"
      >
        {/* Gamification Moment Banner */}
        <MomentBanner moment={moment} className="mb-5" />
        
        <div
          className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600 mb-3"
          data-testid="warmup-to-practice-subtopic"
        >
          {subTopic}
        </div>

        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-5 ring-1 ring-emerald-200">
          <Target className="w-10 h-10 text-emerald-600" />
        </div>

        <h2
          className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
          data-testid="warmup-to-practice-headline"
        >
          Practice mode — on!
        </h2>

        <p
          className="text-slate-700 text-base sm:text-lg leading-relaxed mb-7"
          data-testid="warmup-to-practice-message"
        >
          Warm-up done! Time to build some confidence — one question at a time!
        </p>

        <Button
          onClick={onProceed}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-base font-semibold tap-44"
          data-testid="warmup-to-practice-cta"
        >
          Let&apos;s go <ArrowRight className="w-5 h-5 ml-1.5" />
        </Button>
      </Card>

      <style>{`
        @keyframes spark-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
        }
        .spark-fall {
          animation-name: spark-fall;
          animation-timing-function: cubic-bezier(.2,.6,.4,1);
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}
